import { Modal } from '@/components/admin/Modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { variantForTag } from '@/lib/tag-color';
import type { ResponsePromptTreeNode } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface FlatFile {
  id: string;
  path: string;
  slug: string | null;
}

function flattenFiles(
  tree: ResponsePromptTreeNode[],
  parentPath: string[] = [],
  acc: FlatFile[] = [],
): FlatFile[] {
  for (const n of tree) {
    const path = [...parentPath, n.name];
    if (n.type === 'file') acc.push({ id: n.id, path: path.join(' / '), slug: n.slug });
    if (n.children.length) flattenFiles(n.children, path, acc);
  }
  return acc;
}

interface Props {
  open: boolean;
  onClose: () => void;
  tree: ResponsePromptTreeNode[];
}

/**
 * 여러 프롬프트의 "현재 활성 버전" 에 태그 추가/제거 일괄 적용.
 * 활성 버전이 없는 파일은 자동 skip — 응답에 skipped 수 포함.
 */
export function BulkTagsModal({ open, onClose, tree }: Props) {
  const qc = useQueryClient();
  const files = useMemo(() => flattenFiles(tree), [tree]);

  const [filter, setFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addInput, setAddInput] = useState('');
  const [addTags, setAddTags] = useState<string[]>([]);
  const [removeInput, setRemoveInput] = useState('');
  const [removeTags, setRemoveTags] = useState<string[]>([]);
  const [resultMsg, setResultMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setAddInput('');
      setAddTags([]);
      setRemoveInput('');
      setRemoveTags([]);
      setResultMsg(null);
      setFilter('');
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return files;
    return files.filter(
      (f) => f.path.toLowerCase().includes(q) || f.slug?.toLowerCase().includes(q),
    );
  }, [files, filter]);

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };
  const selectAllFiltered = () => {
    const next = new Set(selectedIds);
    for (const f of filtered) next.add(f.id);
    setSelectedIds(next);
  };
  const clearSelection = () => setSelectedIds(new Set());

  const addAddTag = () => {
    const t = addInput.trim();
    if (!t || addTags.includes(t)) {
      setAddInput('');
      return;
    }
    setAddTags([...addTags, t]);
    setAddInput('');
  };
  const addRemoveTag = () => {
    const t = removeInput.trim();
    if (!t || removeTags.includes(t)) {
      setRemoveInput('');
      return;
    }
    setRemoveTags([...removeTags, t]);
    setRemoveInput('');
  };

  const mut = useMutation({
    mutationFn: () =>
      api.responsePromptBulkActiveTags({
        ids: [...selectedIds],
        add: addTags,
        remove: removeTags,
      }),
    onSuccess: (res) => {
      setResultMsg(`적용 완료: ${res.updated}건 갱신, ${res.skipped}건 건너뜀 (활성 버전 없음).`);
      qc.invalidateQueries({ queryKey: ['admin', 'response-prompts'] });
    },
  });

  const canSubmit =
    selectedIds.size > 0 && (addTags.length > 0 || removeTags.length > 0) && !mut.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="일괄 태그 적용"
      description="선택한 프롬프트들의 현재 활성 버전에 태그를 추가하거나 제거합니다."
      size="xl"
      footer={
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-foreground-muted">
            선택: <strong className="text-foreground">{selectedIds.size}</strong>건
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={mut.isPending}>
              닫기
            </Button>
            <Button type="button" onClick={() => mut.mutate()} disabled={!canSubmit}>
              {mut.isPending ? '적용 중…' : '적용'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* 추가/제거 태그 입력 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-foreground-muted mb-1">추가할 태그</div>
            <div className="flex gap-2">
              <Input
                value={addInput}
                onChange={(e) => setAddInput(e.target.value)}
                placeholder="예: production"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    addAddTag();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={addAddTag}
                disabled={!addInput.trim()}
                className="shrink-0 whitespace-nowrap"
              >
                +
              </Button>
            </div>
            {addTags.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {addTags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-400"
                  >
                    + {t}
                    <button
                      type="button"
                      onClick={() => setAddTags(addTags.filter((x) => x !== t))}
                      className="hover:text-destructive"
                      aria-label="제거"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <div className="text-xs text-foreground-muted mb-1">제거할 태그</div>
            <div className="flex gap-2">
              <Input
                value={removeInput}
                onChange={(e) => setRemoveInput(e.target.value)}
                placeholder="예: draft"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    addRemoveTag();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={addRemoveTag}
                disabled={!removeInput.trim()}
                className="shrink-0 whitespace-nowrap"
              >
                +
              </Button>
            </div>
            {removeTags.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {removeTags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-xs text-destructive"
                  >
                    − {t}
                    <button
                      type="button"
                      onClick={() => setRemoveTags(removeTags.filter((x) => x !== t))}
                      className="hover:text-foreground"
                      aria-label="제거"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* 파일 목록 */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Input
              placeholder="경로 / slug 검색…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-8 text-sm flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={selectAllFiltered}
              disabled={filtered.length === 0}
            >
              필터 결과 전체 선택
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              disabled={selectedIds.size === 0}
            >
              선택 해제
            </Button>
          </div>
          <div className="max-h-64 overflow-y-auto rounded-md border border-border divide-y divide-border">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-foreground-muted">
                대상 파일이 없습니다.
              </div>
            ) : (
              filtered.map((f) => (
                <label
                  key={f.id}
                  className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface-muted/60"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(f.id)}
                    onChange={() => toggle(f.id)}
                    className="size-3.5 rounded border-border shrink-0"
                  />
                  <span className="truncate flex-1">{f.path}</span>
                  {f.slug ? (
                    <Badge variant={variantForTag('default')} className="font-mono text-[10px]">
                      {f.slug}
                    </Badge>
                  ) : null}
                </label>
              ))
            )}
          </div>
        </div>

        {resultMsg ? (
          <div className="rounded-md bg-accent/10 px-3 py-2 text-sm text-accent">{resultMsg}</div>
        ) : null}
        {mut.error ? (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {mut.error instanceof ApiError ? mut.error.message : '실패'}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
